import React, { useEffect, useState } from 'react';
import { Box, Button, Slider, TextField, Typography, FormControl, InputLabel, Select, MenuItem, ButtonGroup, Alert, Tooltip, Tabs, Tab, IconButton, List, ListItem, Paper, Autocomplete } from '@mui/material';
import { usePersona } from '../../lib/hooks/UsePersona';
import { usePrompt } from '../../lib/hooks/UsePrompt';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';
import AddBoxIcon from '@mui/icons-material/AddBox';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { PROMPT_COMPONENT_TYPES } from '@common/domain/types';
import { ENABLE_RESPONSE_MAX_TOKENS } from '@common/domain/constants';
import { isRestrictedUser } from '@common/domain/auth';
import { getPersonaDisplayName } from '../../lib/helpers/personaDisplay';

function PromptConfigEditor({ config, onChange, availableComponents }) {
  const handleAddSimple = () => {
    onChange([...config, { id: '', type: '' }]);
  };

  const handleAddConditional = () => {
    onChange([...config, { parameterKey: '', mapping: {} }]);
  };

  const handleRemove = (index) => {
    const newConfig = [...config];
    newConfig.splice(index, 1);
    onChange(newConfig);
  };

  const handleMove = (index, direction) => {
    const newConfig = [...config];
    const element = newConfig.splice(index, 1)[0];
    newConfig.splice(index + direction, 0, element);
    onChange(newConfig);
  };

  const handleItemChange = (index, newItem) => {
    const newConfig = [...config];
    newConfig[index] = newItem;
    onChange(newConfig);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1400 }}>
      <Typography variant="h6" gutterBottom>Execution Sequence</Typography>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
        }}
      >
        <List>
          {config.map((item, index) => (
            <React.Fragment key={index}>
              <ListItem
                sx={{
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  bgcolor: 'action.hover',
                  mb: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ flexGrow: 1, color: 'text.primary' }}>
                    Step {index + 1}: {item.parameterKey
                      ? `Conditional Logic (${item.parameterKey})`
                      : `Simple Component (${item.type && item.id ? `${item.type} : ${item.id}` : 'Unselected'})`}
                  </Typography>
                  <IconButton size="small" onClick={() => handleMove(index, -1)} disabled={index === 0}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleMove(index, 1)} disabled={index === config.length - 1}>
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleRemove(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                {item.parameterKey !== undefined ? (
                  // Conditional Item
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Parameter Key"
                      size="small"
                      value={item.parameterKey}
                      onChange={(e) => handleItemChange(index, { ...item, parameterKey: e.target.value })}
                      placeholder="e.g. responseMode"
                    />
                    <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}>
                      <Typography variant="caption" display="block" gutterBottom sx={{ color: 'text.secondary' }}>Mappings (Value &rarr; Component)</Typography>
                      {Object.entries(item.mapping).map(([val, comp], mIdx) => (
                        <Box key={val} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                          <TextField
                            label="Value"
                            size="small"
                            value={val}
                            sx={{
                              width: 150,
                            }}
                            onChange={(e) => {
                              const newMapping = { ...item.mapping };
                              const oldVal = val;
                              const newVal = e.target.value;
                              if (newVal !== oldVal) {
                                newMapping[newVal] = newMapping[oldVal];
                                delete newMapping[oldVal];
                                handleItemChange(index, { ...item, mapping: newMapping });
                              }
                            }}
                          />
                          <FormControl size="small" sx={{
                            flexGrow: 1,
                          }}>
                            <InputLabel>Component</InputLabel>
                            <Select
                              value={`${comp.type}:${comp.id}`}
                              label="Component"
                              onChange={(e) => {
                                const [type, id] = e.target.value.split(':');
                                const newMapping = { ...item.mapping };
                                newMapping[val] = { type, id };
                                handleItemChange(index, { ...item, mapping: newMapping });
                              }}
                              renderValue={(selected) => {
                                const [type, id] = selected.split(':');
                                return id ? `${type} : ${id}` : 'Select Component';
                              }}
                            >
                              {PROMPT_COMPONENT_TYPES.map(type => [
                                <MenuItem key={`header-${type}`} disabled sx={{ fontWeight: 'bold', opacity: '1 !important' }}>
                                  {type}
                                </MenuItem>,
                                ...availableComponents
                                  .filter(c => c.type === type)
                                  .map(c => (
                                    <MenuItem key={`${c.type}:${c.id}`} value={`${c.type}:${c.id}`} sx={{ pl: 4 }}>
                                      {c.type} : {c.id}
                                    </MenuItem>
                                  ))
                              ])}
                              {/* Also show components with unknown types */}
                              {(() => {
                                const knownTypes = new Set(PROMPT_COMPONENT_TYPES);
                                const unknownComponents = availableComponents.filter(c => !knownTypes.has(c.type));
                                if (unknownComponents.length > 0) {
                                  return [
                                    <MenuItem key="header-OTHER" disabled sx={{ fontWeight: 'bold', opacity: '1 !important' }}>OTHER</MenuItem>,
                                    ...unknownComponents.map(c => (
                                      <MenuItem key={`${c.type}:${c.id}`} value={`${c.type}:${c.id}`} sx={{ pl: 4 }}>
                                        {c.type} : {c.id}
                                      </MenuItem>
                                    ))
                                  ];
                                }
                                return null;
                              })()}
                            </Select>
                          </FormControl>
                          <IconButton size="small" color="error" onClick={() => {
                            const newMapping = { ...item.mapping };
                            delete newMapping[val];
                            handleItemChange(index, { ...item, mapping: newMapping });
                          }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const newMapping = { ...item.mapping };
                          newMapping[`value${Object.keys(item.mapping).length}`] = { id: '', type: '' };
                          handleItemChange(index, { ...item, mapping: newMapping });
                        }}
                      >
                        Add Mapping
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  // Simple Item
                  <FormControl size="small" fullWidth>
                    <InputLabel>Component</InputLabel>
                    <Select
                      value={`${item.type}:${item.id}`}
                      label="Component"
                      onChange={(e) => {
                        const [type, id] = e.target.value.split(':');
                        handleItemChange(index, { type, id });
                      }}
                      renderValue={(selected) => {
                        const [type, id] = selected.split(':');
                        return id ? `${type} : ${id}` : 'Select Component';
                      }}
                    >
                      {PROMPT_COMPONENT_TYPES.map(type => [
                        <MenuItem key={`header-${type}`} disabled sx={{ fontWeight: 'bold', opacity: '1 !important' }}>
                          {type}
                        </MenuItem>,
                        ...availableComponents
                          .filter(c => c.type === type)
                          .map(c => (
                            <MenuItem key={`${c.type}:${c.id}`} value={`${c.type}:${c.id}`} sx={{ pl: 4 }}>
                              {c.type} : {c.id}
                            </MenuItem>
                          ))
                      ])}
                      {/* Also show components with unknown types */}
                      {(() => {
                        const knownTypes = new Set(PROMPT_COMPONENT_TYPES);
                        const unknownComponents = availableComponents.filter(c => !knownTypes.has(c.type));
                        if (unknownComponents.length > 0) {
                          return [
                            <MenuItem key="header-OTHER" disabled sx={{ fontWeight: 'bold', opacity: '1 !important' }}>OTHER</MenuItem>,
                            ...unknownComponents.map(c => (
                              <MenuItem key={`${c.type}:${c.id}`} value={`${c.type}:${c.id}`} sx={{ pl: 4 }}>
                                {c.type} : {c.id}
                              </MenuItem>
                            ))
                          ];
                        }
                        return null;
                      })()}
                    </Select>
                  </FormControl>
                )}
              </ListItem>
            </React.Fragment>
          ))}
        </List>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddSimple}>
            Add Component
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddConditional}>
            Add Conditional
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default function CreatorPortal() {
  const isLocalDev = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const RESPONSE_MAX_TOKENS_DEFAULT = 9999;
  const [state, setState] = useState({
    personas: [],
    draft: {}, // Store only changed values
    saving: {},
    errors: {},
    selectedPersonaId: null,
    isCreatingNewPersona: false
  });
  const { userInfo } = useUserInfoContext()

  const handlePersonaChange = (newPersonaId) => {
    if (hasChanges(state.selectedPersonaId)) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to switch personas?')) {
        return;
      }
    }
    setState(prev => ({
      ...prev,
      selectedPersonaId: newPersonaId,
      isCreatingNewPersona: false,
      draft: {} // Clear draft when switching personas
    }));
  };

  const handleNewPersonaClick = () => {
    if (hasChanges(state.selectedPersonaId)) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to create a new persona?')) {
        return;
      }
    }
    setState(prev => ({
      ...prev,
      isCreatingNewPersona: true,
      selectedPersonaId: null,
      draft: {
        new: {
          id: '',
          replicaId: '',
          devPersonaId: '',
          localPersonaId: '',
          voiceId: '',
          tenantId: 'chip',
          category: 'LOTE2',
          name: '',
          temperature: 0,
          responseMaxTokens: RESPONSE_MAX_TOKENS_DEFAULT,
          imageURL: '',
          personality: '',
          fileName: ''
        }
      },
      errors: {}
    }));
  };

  const handleCancelEdit = (personaId) => {
    if (hasChanges(personaId)) {
        if (!window.confirm('Are you sure you want to discard your changes?')) {
            return;
        }
    }
    setState(prev => {
        const { [personaId]: _, ...remainingDraft } = prev.draft;
        return {
            ...prev,
            draft: remainingDraft,
            errors: {}
        };
    });
  };

  const handleCancelNewPersona = () => {
    const draft = state.draft.new || {};
    if (draft.id || draft.replicaId || draft.name || draft.tenantId !== 'chip' || draft.category !== 'LOTE2' || draft.temperature !== 0 || draft.responseMaxTokens !== RESPONSE_MAX_TOKENS_DEFAULT || draft.imageURL || draft.personality) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        return;
      }
    }
      setState(prev => {
        const chip = prev.personas.find(p => p.id === 'p072690a2e4b') || prev.personas[0];
        return {
          ...prev,
          isCreatingNewPersona: false,
          selectedPersonaId: chip?.id || null, // Select Chip v5 or first persona or null if none exist
          draft: {},
          errors: {}
        };
      });
  };


  const { getPersonas, updatePersona, createPersona, deletePersona: deletePersonaApi } = usePersona();
  const { getAllComponents } = usePrompt();
  const [availableComponents, setAvailableComponents] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const availableTenants = React.useMemo(() => {
    const safePersonas = Array.isArray(state?.personas) ? state.personas : [];
    const tenants = Array.from(new Set(safePersonas.map(p => p.tenantId || 'chip')));
    if (!tenants.includes('chip')) tenants.push('chip');
    if (!tenants.includes('ed')) tenants.push('ed');
    return tenants.sort();
  }, [state?.personas]);

  const availableCategories = React.useMemo(() => {
    const safePersonas = Array.isArray(state?.personas) ? state.personas : [];
    const categories = Array.from(new Set(safePersonas.map(p => p.category).filter(Boolean)));
    if (!categories.includes('LOTE')) categories.push('LOTE');
    if (!categories.includes('LOTE2')) categories.push('LOTE2');
    if (!categories.includes('EWP')) categories.push('EWP');
    return categories.sort();
  }, [state?.personas]);

  const resolvedComponents = React.useMemo(() => {
    const persona = state.isCreatingNewPersona
      ? { promptConfig: [] }
      : state.personas.find(p => p.id === state.selectedPersonaId);

    if (!persona) return {};

    const draft = state.isCreatingNewPersona
      ? (state.draft.new || {})
      : (state.draft[persona.id] || {});

    const config = draft.promptConfig || persona.promptConfig || [];

    const results = {};
    const typesToResolve = [
      'IDENTITY', 'AUDIO_STYLE', 'THEOLOGY',
      'GUIDANCE_SMALL_TALK', 'GUIDANCE_STRAIGHT_TALK', 'GUIDANCE_ALIGNED',
      'GUIDANCE_CAUTION', 'GUIDANCE_NEUTRAL_SEGUE', 'GUIDANCE_BLOCKED'
    ];

    const findId = (type) => {
      for (const item of config) {
        if (item.type === type) return item.id;
        if (item.mapping) {
          for (const mItem of Object.values(item.mapping)) {
            if (mItem.type === type) return mItem.id;
          }
        }
      }
      return null;
    };

    for (const type of typesToResolve) {
      const id = findId(type);
      if (id) {
        const comp = availableComponents.find(c => c.type === type && c.id === id);
        if (comp) results[type] = comp.content;
      }
    }

    return results;
  }, [state.selectedPersonaId, state.isCreatingNewPersona, state.personas, state.draft, availableComponents]);

  useEffect(() => {
    const fetchComponents = async () => {
      try {
        const data = await getAllComponents();
        setAvailableComponents(data);
      } catch (err) {
        console.error('Failed to load components', err);
      }
    };
    fetchComponents();
  }, [getAllComponents]);

  const hasChanges = (personaId) => {
    if (state.isCreatingNewPersona) {
      const draft = state.draft.new || {};
      return draft.personaId || draft.id || draft.replicaId || draft.name || draft.devPersonaId || draft.localPersonaId || draft.temperature !== 0 || draft.responseMaxTokens === RESPONSE_MAX_TOKENS_DEFAULT || draft.imageURL || draft.personality;
    }
    return Object.keys(state.draft[personaId] || {}).length > 0;
  };

  const handleFieldChange = (personaId, field, newValue) => {
    if (state.isCreatingNewPersona) {
      setState(prev => ({
        ...prev,
        draft: {
          new: {
            ...prev.draft.new,
            [field]: newValue
          }
        }
      }));
      return;
    }

    const persona = state.personas.find(p => p.id === personaId);
    const existingValue = persona ? persona[field] : undefined;
    const isBothEmpty =
      (existingValue === undefined || existingValue === null || existingValue === '') &&
      (newValue === undefined || newValue === null || newValue === '');
    // Simple equality check for basic fields, deep for objects if needed
    const isSame = isBothEmpty || JSON.stringify(existingValue) === JSON.stringify(newValue);

    if (isSame) {
      setState(prev => {
        const currentDraft = prev.draft[personaId] || {};
        const { [field]: _, ...remainingDraftForPersona } = currentDraft;

        if (Object.keys(remainingDraftForPersona).length === 0) {
            const { [personaId]: __, ...remainingDraft } = prev.draft;
            return { ...prev, draft: remainingDraft };
        }

        return {
          ...prev,
          draft: {
            ...prev.draft,
            [personaId]: remainingDraftForPersona
          }
        };
      });
      return;
    }

    setState(prev => ({
      ...prev,
      draft: {
        ...prev.draft,
        [personaId]: {
          ...prev.draft[personaId],
          [field]: newValue
        }
      }
    }));
  };

  const handleModeGuidanceChange = (personaId, mode, newValue) => {
    const persona = state.personas.find(p => p.id === personaId) || {};
    const currentDraft = state.draft[personaId] || {};
    const currentModeGuidance = currentDraft.modeGuidance || persona.modeGuidance || {};

    const newModeGuidance = {
      ...currentModeGuidance,
      [mode]: newValue
    };

    handleFieldChange(personaId, 'modeGuidance', newModeGuidance);
  };

  const handleSave = async (personaId) => {
    if (state.isCreatingNewPersona) {
      const newPersona = state.draft.new;

      const errors = {};
      if (!newPersona.name) errors.name = "Name is required";
      if (!newPersona.category) errors.category = "Category is required";
      if (!newPersona.id) errors.id = "Vendor Persona ID is required";
      if (state.personas.find(persona => persona.id === newPersona.id)) {
        errors.id = "Vendor Persona ID already exists";
      }

      if (Object.keys(errors).length) {
        setState(prev => ({ ...prev, errors }));
        return;
      }

      setState(prev => ({
        ...prev,
        saving: { new: true },
        errors: {}
      }));

      try {
        await createPersona(newPersona);

        // Update state with new persona
        setState(prev => ({
          ...prev,
          personas: [...prev.personas, newPersona],
          selectedPersonaId: newPersona.id,
          isCreatingNewPersona: false,
          draft: {},
          saving: {},
          errors: { success: "Persona saved successfully." }
        }));
      } catch (error) {
        console.error('Error creating new persona:', error);
        setState(prev => ({
          ...prev,
          saving: {},
          errors: { message: "Something went wrong." }
        }));
      }
      return;
    }

    // Handle existing persona updates
    const changes = state.draft[personaId] || {};

    setState(prev => ({
      ...prev,
      saving: { ...prev.saving, [personaId]: true },
      errors: { ...prev.errors, [personaId]: false }
    }));

    try {
      await updatePersona(personaId, changes);

      // After successful save, update persona and clear draft
      setState(prev => {
        const newPersonas = prev.personas.map(p => 
          p.id === personaId 
            ? { ...p, ...changes }
            : p
        );
        const { [personaId]: _, ...remainingDraft } = prev.draft;
        const { [personaId]: __, ...remainingSaving } = prev.saving;
        return {
          ...prev,
          personas: newPersonas,
          draft: remainingDraft,
          saving: remainingSaving
        };
      });
    } catch (error) {
      console.error('Error saving changes:', error);
      setState(prev => ({
        ...prev,
        saving: { ...prev.saving, [personaId]: false },
        errors: { ...prev.errors, [personaId]: true, message: "Something went wrong." }
      }));
    }
  };

  const handleDeletePersona = async (personaId) => {
    if (!window.confirm(`Are you sure you want to delete persona ${personaId}?`)) {
      return;
    }

    try {
      await deletePersonaApi(personaId);
      setState(prev => {
        const newPersonas = prev.personas.filter(p => p.id !== personaId);
        const chip = newPersonas.find(p => p.id === 'p072690a2e4b') || newPersonas[0];
        return {
          ...prev,
          personas: newPersonas,
          selectedPersonaId: chip?.id || null,
          draft: {},
          errors: { success: "Persona deleted successfully." }
        };
      });
    } catch (error) {
      console.error('Error deleting persona:', error);
      setState(prev => ({
        ...prev,
        errors: { message: "Failed to delete persona." }
      }));
    }
  };

  /* Only fetch 1st load ignore dependencies */
  /* eslint-disable */
  useEffect(() => {
    if (userInfo) {
      const getAllPersonas = async () => {
        try {
          const response = await getPersonas(userInfo);
          const safePersonas = Array.isArray(response) ? response : [];
          setState(prev => {
            const chip = safePersonas.find(p => p.id === 'p072690a2e4b') || safePersonas[0];
            return {
              ...prev,
              personas: safePersonas,
              selectedPersonaId: chip?.id || undefined // Select Chip v5 or first persona by default
            };
          });
        } catch (err) {
          console.error('Error fetching personas in CreatorPortal:', err);
          setState(prev => ({
            ...prev,
            errors: { message: "Failed to load personas. Please refresh or try again later." }
          }));
        }
      }
      getAllPersonas()
    }
  }, [])
  /* eslint-enable */

  const marks = [
    {
      value: 0,
      label: '0',
    },
    {
      value: 0.5,
      label: '0.5',
    },
    {
      value: 1,
      label: '1',
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', padding: '24px 32px', minHeight: 'calc(100vh - 48px)' }}>
          <Typography variant='h4' gutterBottom>Digital Human Creator Portal</Typography>
            <Box sx={{ display: 'flex', gap: 2, marginTop: '10px', marginBottom: '1em' }}>
              <ButtonGroup>
                <Box sx={{ width: 600 }}>
                  <InputLabel sx={{fontSize: 12}}>Digital Human</InputLabel>
                  <FormControl fullWidth>
                    <Select
                      value={state.selectedPersonaId || ''}
                      onChange={(e) => handlePersonaChange(e.target.value)}
                      disabled={state.isCreatingNewPersona}
                    >
                      {[...state.personas].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(persona => (
                        <MenuItem key={persona.id} value={persona.id}>
                          {getPersonaDisplayName(persona)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Button 
                  variant="outlined"
                  onClick={handleNewPersonaClick}
                  sx={{ height: 56, marginTop: 'auto' }}
                >
                  <AddBoxIcon />
                </Button>
              </ButtonGroup>
            </Box>

            {state.errors.success && <Alert severity="success" sx={{ mb: 2, width: '100%' }}>{state.errors.success}</Alert>}
            {state.errors.message && <Alert severity="error" sx={{ mb: 2, width: '100%' }}>{state.errors.message}</Alert>}

            {(state.selectedPersonaId || state.isCreatingNewPersona) && (() => {
                const persona = state.isCreatingNewPersona 
                  ? {
                      name: '',
                      category: 'LOTE2',
                      temperature: 0,
                      personality: '',
                      responseMaxTokens: RESPONSE_MAX_TOKENS_DEFAULT,
                      identity: '',
                      audioStyle: '',
                      theologicalFrameworks: '',
                      modeGuidance: {
                        smallTalk: '',
                        straightTalk: '',
                        aligned: '',
                        caution: '',
                        neutralSegue: '',
                        blocked: ''
                      },
                      promptConfig: []
                    }
                  : state.personas.find(p => p.id === state.selectedPersonaId);

                const draft = state.isCreatingNewPersona 
                  ? state.draft.new 
                  : (state.draft[persona.id] || {});

                const getValue = (field, defaultValue = '') => {
                  if (draft.hasOwnProperty(field)) return draft[field];
                  if (persona[field] !== undefined && persona[field] !== null) return persona[field];
                  return defaultValue;
                };

                const getDynamicHelperText = () => {
                  const currentId = (draft.id || '').trim().replace(/[^a-zA-Z0-9_-]/g, '') || 'ID';
                  let prefix = (draft.fileName || '').trim();
                  if (prefix.toLowerCase().endsWith('.ts')) {
                    prefix = prefix.slice(0, -3);
                  }
                  let baseName = prefix.replace(/[^a-zA-Z0-9_-]/g, '');
                  if (baseName.toLowerCase().endsWith('-persona')) {
                    baseName = baseName.slice(0, -8);
                  }

                  const finalName = baseName ? `${baseName}-persona.ts` : `${currentId}-persona.ts`;
                  return `Creates: ${finalName} (defaults to ${currentId}-persona.ts)`;
                };

                const getResolvedValue = (type, field) => {
                  // Priority: Draft field > Resolved Component Content > Persona field
                  if (draft.hasOwnProperty(field)) return draft[field];
                  if (resolvedComponents[type]) return resolvedComponents[type];
                  return persona[field] || '';
                };

                const getModeGuidanceValue = (mode) => {
                  const modeToComponentType = {
                    smallTalk: 'GUIDANCE_SMALL_TALK',
                    straightTalk: 'GUIDANCE_STRAIGHT_TALK',
                    aligned: 'GUIDANCE_ALIGNED',
                    caution: 'GUIDANCE_CAUTION',
                    neutralSegue: 'GUIDANCE_NEUTRAL_SEGUE',
                    blocked: 'GUIDANCE_BLOCKED'
                  };

                  // Priority: Draft modeGuidance > Resolved Component Content > Persona modeGuidance
                  const draftMg = draft.modeGuidance || {};
                  if (draftMg.hasOwnProperty(mode)) return draftMg[mode];

                  const type = modeToComponentType[mode];
                  if (resolvedComponents[type]) return resolvedComponents[type];

                  const mg = persona.modeGuidance || {};
                  return mg[mode] || '';
                };

                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', mb: 2 }}>
                          {state.isCreatingNewPersona ? (
                            <Box sx={{ width: 600 }}>
                              <TextField
                                label="Digital Human Name"
                                fullWidth
                                value={draft.name}
                                onChange={(e) => handleFieldChange('new', 'name', e.target.value)}
                                error={!!state.errors.name}
                                helperText={state.errors.name}
                                required
                              />
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: 24 }}>
                                {getPersonaDisplayName(persona)}
                            </Typography>
                          )}

                          <Box sx={{ display: 'flex', gap: 2 }}>
                            {!state.isCreatingNewPersona && (
                              hasChanges(persona.id) ? (
                                <Button
                                    variant="outlined"
                                    onClick={() => handleCancelEdit(persona.id)}
                                >
                                    Cancel
                                </Button>
                              ) : (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleDeletePersona(persona.id)}
                                >
                                    Delete Persona
                                </Button>
                              )
                            )}
                            <Button
                                variant='contained'
                                onClick={() => state.isCreatingNewPersona ? handleSave() : handleSave(persona.id)}
                                disabled={
                                  state.isCreatingNewPersona
                                    ? !draft.name || !draft.id || !!Object.keys(state.errors).length || state.saving.new
                                    : (!hasChanges(persona.id) || state.saving[persona.id])
                                }
                                color="success"
                            >
                                {(state.isCreatingNewPersona ? state.saving.new : state.saving[persona.id])
                                  ? 'Saving...'
                                  : 'Save Changes'}
                            </Button>
                            {state.isCreatingNewPersona && (
                              <Button
                                variant='outlined'
                                onClick={handleCancelNewPersona}
                                disabled={state.saving.new}
                              >
                                Cancel
                              </Button>
                            )}
                          </Box>
                        </Box>

                        {state.isCreatingNewPersona && (
                          <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
                            {isLocalDev ? (
                              <span>
                                💻 <strong>Local Development:</strong> Saving this new persona will automatically generate a TypeScript template file in your local codebase at <code style={{ backgroundColor: 'rgba(0,0,0,0.06)', padding: '2px 4px', borderRadius: 4 }}>backend/src/config/personas/&lt;id&gt;-persona.ts</code> and register it in DynamoDB.
                              </span>
                            ) : (
                              <span>
                                🚀 <strong>Production Environment:</strong> Saving this new persona will write its configuration and prompt components directly to DynamoDB (and skip local codebase file generation).
                              </span>
                            )}
                          </Alert>
                        )}
                        {hasChanges(persona.id) && (
                            <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>Values have changed, please make sure to save your changes!</Alert>
                        )}

                        <Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%', mb: 2 }}>
                            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                                <Tab label="General" />
                                <Tab label="Details" />
                                <Tab label="Mode Guidance" />
                                <Tab label="Prompt Config" />
                            </Tabs>
                        </Box>

                        {/* TAB 0: General */}
                        {activeTab === 0 && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                {state.isCreatingNewPersona && (
                                    <TextField
                                        label="Vendor Persona ID"
                                        value={draft.id}
                                        onChange={(e) => handleFieldChange('new', 'id', e.target.value)}
                                        error={!!state.errors.id}
                                        helperText={state.errors.id}
                                        required
                                        sx={{ flexGrow: 1 }}
                                    />
                                )}
                                {state.isCreatingNewPersona && (
                                    <TextField
                                        label="On-disk File Name Prefix"
                                        placeholder="e.g. folo"
                                        value={draft.fileName || ''}
                                        onChange={(e) => handleFieldChange('new', 'fileName', e.target.value)}
                                        sx={{ flexGrow: 1 }}
                                        helperText={getDynamicHelperText()}
                                    />
                                )}
                                <FormControl sx={{ flexGrow: 1 }}>
                                    <InputLabel>Tenant ID</InputLabel>
                                    <Select
                                        value={getValue('tenantId') || 'chip'}
                                        label="Tenant ID"
                                        onChange={(e) => handleFieldChange(state.isCreatingNewPersona ? 'new' : persona.id, 'tenantId', e.target.value)}
                                    >
                                        {availableTenants.map(t => (
                                          <MenuItem key={t} value={t}>{t}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {state.isCreatingNewPersona ? (
                                  isRestrictedUser(userInfo?.groups) ? (
                                    <TextField
                                      sx={{ flexGrow: 1 }}
                                      value={getValue('category') || 'LOTE2'}
                                      disabled
                                      label="Category"
                                      variant="outlined"
                                      size="medium"
                                    />
                                  ) : (
                                    <Autocomplete
                                      freeSolo
                                      options={availableCategories}
                                      value={getValue('category') || 'LOTE2'}
                                      onChange={(_, newValue) => handleFieldChange('new', 'category', newValue || '')}
                                      onInputChange={(_, newInputValue) => handleFieldChange('new', 'category', newInputValue || '')}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Category"
                                          error={!!state.errors.category}
                                          helperText={state.errors.category || "Select from existing or type a custom category"}
                                          required
                                        />
                                      )}
                                      sx={{ flexGrow: 1 }}
                                    />
                                  )
                                ) : (
                                  <TextField
                                    sx={{ flexGrow: 1 }}
                                    value={getValue('category')}
                                    disabled
                                    label="Category"
                                    variant="outlined"
                                    size="medium"
                                  />
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography sx={{ textAlign: "left" }}>
                                        Temperature
                                        <Tooltip title="Controls randomness: Higher = more creative, Lower = more deterministic."><HelpOutlineIcon sx={{ ml: 1, fontSize: 16 }} /></Tooltip>
                                    </Typography>
                                    <Slider
                                        value={Number(getValue('temperature', 0))}
                                        onChange={(_, v) => handleFieldChange(state.isCreatingNewPersona ? 'new' : persona.id, 'temperature', v)}
                                        step={0.1}
                                        marks={marks}
                                        min={0}
                                        max={1}
                                        valueLabelDisplay="auto"
                                    />
                                </Box>
                            {ENABLE_RESPONSE_MAX_TOKENS && (
                                <TextField
                                    label="Response Max Tokens"
                                    type="number"
                                    value={getValue('responseMaxTokens', RESPONSE_MAX_TOKENS_DEFAULT)}
                                    onChange={(e) => handleFieldChange(state.isCreatingNewPersona ? 'new' : persona.id, 'responseMaxTokens', parseInt(e.target.value))}
                                    sx={{ flexGrow: 1 }}
                                />
                            )}
                            </Box>
                            <TextField
                                label="Image URL"
                                fullWidth
                                value={getValue('imageURL')}
                                onChange={(e) => handleFieldChange(state.isCreatingNewPersona ? 'new' : persona.id, 'imageURL', e.target.value)}
                            />
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label="Voice ID (ElevenLabs)"
                                    placeholder="e.g. jmx9jL3dqMTIH9azJH72"
                                    value={getValue('voiceId')}
                                    onChange={(e) => handleFieldChange(state.isCreatingNewPersona ? 'new' : persona.id, 'voiceId', e.target.value)}
                                    sx={{ flexGrow: 1 }}
                                />
                                <TextField
                                    label="Replica ID (Tavus)"
                                    placeholder="e.g. r8ba3e96f122"
                                    value={getValue('replicaId')}
                                    onChange={(e) => handleFieldChange(state.isCreatingNewPersona ? 'new' : persona.id, 'replicaId', e.target.value)}
                                    sx={{ flexGrow: 1 }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label="Dev Persona ID (Tavus Override for DEV)"
                                    placeholder="e.g. p12345dev"
                                    value={getValue('devPersonaId')}
                                    onChange={(e) => handleFieldChange(state.isCreatingNewPersona ? 'new' : persona.id, 'devPersonaId', e.target.value)}
                                    sx={{ flexGrow: 1 }}
                                />
                                <TextField
                                    label="Local Persona ID (Tavus Override for LOCAL)"
                                    placeholder="e.g. p12345local"
                                    value={getValue('localPersonaId')}
                                    onChange={(e) => handleFieldChange(state.isCreatingNewPersona ? 'new' : persona.id, 'localPersonaId', e.target.value)}
                                    sx={{ flexGrow: 1 }}
                                />
                            </Box>
                          </Box>
                        )}

                        {/* TAB 1: Details */}
                        {activeTab === 1 && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                            <TextField
                                label="Personality"
                                multiline
                                rows={4}
                                fullWidth
                                value={getValue('personality')}
                                onChange={(e) => handleFieldChange(state.isCreatingNewPersona ? 'new' : persona.id, 'personality', e.target.value)}
                            />
                            <TextField
                                label="Identity"
                                multiline
                                rows={6}
                                fullWidth
                                value={getResolvedValue('IDENTITY', 'identity')}
                                onChange={(e) => handleFieldChange(state.isCreatingNewPersona ? 'new' : persona.id, 'identity', e.target.value)}
                            />
                            <TextField
                                label="Audio Style"
                                multiline
                                rows={4}
                                fullWidth
                                value={getResolvedValue('AUDIO_STYLE', 'audioStyle')}
                                onChange={(e) => handleFieldChange(state.isCreatingNewPersona ? 'new' : persona.id, 'audioStyle', e.target.value)}
                            />
                            <TextField
                                label="Theological Frameworks"
                                multiline
                                rows={4}
                                fullWidth
                                value={getResolvedValue('THEOLOGY', 'theologicalFrameworks')}
                                onChange={(e) => handleFieldChange(state.isCreatingNewPersona ? 'new' : persona.id, 'theologicalFrameworks', e.target.value)}
                            />
                          </Box>
                        )}

                        {/* TAB 2: Mode Guidance */}
                        {activeTab === 2 && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                            {['smallTalk', 'straightTalk', 'aligned', 'caution', 'neutralSegue', 'blocked'].map(mode => (
                                <TextField
                                    key={mode}
                                    label={`Mode: ${mode.charAt(0).toUpperCase() + mode.slice(1)}`}
                                    multiline
                                    rows={4}
                                    fullWidth
                                    value={getModeGuidanceValue(mode)}
                                    onChange={(e) => handleModeGuidanceChange(state.isCreatingNewPersona ? 'new' : persona.id, mode, e.target.value)}
                                />
                            ))}
                          </Box>
                        )}

                        {/* TAB 3: Prompt Config */}
                        {activeTab === 3 && (
                          <PromptConfigEditor
                            config={getValue('promptConfig') || []}
                            onChange={(newConfig) => handleFieldChange(state.isCreatingNewPersona ? 'new' : persona.id, 'promptConfig', newConfig)}
                            availableComponents={availableComponents}
                          />
                        )}
                    </Box>
                );
            })()}
        </Box>
  );
}